/* ------------------------------------------------------------------
   ChampKoi koi quick-add toggle

   Koi are one-of-a-kind (inventory 1), so a quick-add button that stays
   "Add to cart" after adding just errors "Sold out" on the second click.
   Every koi quick-add button ([data-koi-quick-toggle], rendered by
   card-product.liquid with server-side cart state) instead toggles:

   - not in cart → normal Dawn product-form add (this script does nothing)
   - in cart     → button reads "Remove from cart"; the click is
                   intercepted, the line is removed via /cart/change.js,
                   the cart icon bubble re-renders, and a cartUpdate is
                   published so badges/other listeners re-sync.

   State sync mirrors ck-card-cart-badges.js: server-rendered initial
   state, re-synced from /cart.js on cartUpdate pub/sub events,
   cart:refresh, and bfcache restores.
   ------------------------------------------------------------------ */

(() => {
  if (window.__ckKoiQuickToggleLoaded) return;
  window.__ckKoiQuickToggleLoaded = true;

  const SELECTOR = '.quick-add__submit[data-koi-quick-toggle]';
  const TEXT_ADD = 'Add to cart';
  const TEXT_REMOVE = 'Remove from cart';

  let inCart = null; // Set of numeric variant ids; null until first sync
  let syncTimer = null;

  function applyState(btn) {
    if (!inCart) return;
    const vid = Number(btn.dataset.variantId);
    const remove = inCart.has(vid);
    btn.classList.toggle('ck-quick-remove', remove);
    const label = btn.querySelector('span');
    if (label) label.textContent = remove ? TEXT_REMOVE : TEXT_ADD;
    btn.disabled = false;
  }

  function applyAll() {
    document.querySelectorAll(SELECTOR).forEach(applyState);
  }

  async function syncFromServer() {
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
      const cart = await res.json();
      inCart = new Set((cart.items || []).map((i) => i.variant_id));
      applyAll();
    } catch {
      /* non-critical — leave current state in place */
    }
  }

  function debouncedSync() {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(syncFromServer, 150);
  }

  // Re-render the header cart count after a removal (Dawn updates it via
  // section rendering on add; removals from a grid have no cart section
  // on the page to do it for us).
  async function refreshCartIconBubble() {
    const el = document.getElementById('cart-icon-bubble');
    if (!el) return;
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}?sections=cart-icon-bubble`);
      const sections = await res.json();
      if (!sections['cart-icon-bubble']) return;
      const doc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
      const fresh = doc.getElementById('cart-icon-bubble') || doc.querySelector('.shopify-section') || doc.body;
      el.innerHTML = fresh.innerHTML;
    } catch {
      /* cosmetic only */
    }
  }

  async function removeFromCart(btn) {
    const vid = Number(btn.dataset.variantId);
    btn.disabled = true;
    btn.classList.add('loading');
    try {
      // update.js keys on variant id (change.js wants a line key/index,
      // which a grid card doesn't know).
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ updates: { [vid]: 0 } }),
      });
      if (!res.ok) throw new Error(`cart/update ${res.status}`);
      const cartData = await res.json();
      inCart = new Set((cartData.items || []).map((i) => i.variant_id));
      applyAll();
      refreshCartIconBubble();
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ck-koi-quick-toggle', cartData });
      }
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } catch (err) {
      console.error('[koi-quick-toggle]', err);
      debouncedSync(); // re-derive truth rather than guessing
    } finally {
      btn.disabled = false;
      btn.classList.remove('loading');
    }
  }

  // Intercept BEFORE the form's default submit: preventDefault on the
  // triggering click stops the type=submit button from submitting the
  // product-form, so Dawn's add path never fires in remove mode.
  document.addEventListener('click', (e) => {
    const btn = e.target.closest(SELECTOR);
    if (!btn || !btn.classList.contains('ck-quick-remove')) return;
    e.preventDefault();
    e.stopPropagation();
    if (btn.disabled) return;
    removeFromCart(btn);
  });

  function init() {
    document.addEventListener('cart:refresh', debouncedSync);
    // Bare identifiers — pubsub.js top-level consts don't attach to window.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        // Skip our own publishes — state is already applied.
        if (event && event.source === 'ck-koi-quick-toggle') return;
        debouncedSync();
      });
    }
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) debouncedSync();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
